import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { toast } from 'sonner';
import {
  MessageCircle, Send, Loader2, Search, Building,
  User, RefreshCw, ChevronRight, Clock
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ChatAdmin = () => {
  const { token, user } = useAuth();
  const [conversas, setConversas] = useState([]);
  const [conversaAtual, setConversaAtual] = useState(null);
  const [mensagens, setMensagens] = useState([]);
  const [novaMensagem, setNovaMensagem] = useState('');
  const [loading, setLoading] = useState(true);
  const [enviando, setEnviando] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [totalNaoLidas, setTotalNaoLidas] = useState(0);
  const messagesEndRef = useRef(null);
  const pollIntervalRef = useRef(null);

  useEffect(() => {
    fetchConversas();
    fetchNaoLidas();
    
    // Poll para novas conversas a cada 10 segundos
    pollIntervalRef.current = setInterval(() => {
      fetchConversas();
      fetchNaoLidas();
    }, 10000);
    
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (conversaAtual) {
      fetchMensagens();
      // Poll para mensagens da conversa atual a cada 5 segundos
      const msgPoll = setInterval(fetchMensagens, 5000);
      return () => clearInterval(msgPoll);
    }
  }, [conversaAtual]);

  useEffect(() => {
    scrollToBottom();
  }, [mensagens]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchNaoLidas = async () => {
    try {
      const res = await fetch(`${API_URL}/api/chat/nao-lidas`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setTotalNaoLidas(data.total_nao_lidas);
      }
    } catch (error) {
      console.error('Erro ao buscar não lidas:', error);
    }
  };

  const fetchConversas = async () => {
    try {
      const res = await fetch(`${API_URL}/api/chat/admin/lista`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setConversas(data);
      }
    } catch (error) {
      console.error('Erro ao buscar conversas:', error);
      toast.error('Erro ao carregar conversas');
    } finally {
      setLoading(false);
    }
  };

  const fetchMensagens = async () => {
    if (!conversaAtual) return;
    
    try {
      const res = await fetch(`${API_URL}/api/chat/conversas/${conversaAtual.id}/mensagens`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setMensagens(await res.json());
        fetchNaoLidas();
        // Atualizar lista de conversas para refletir mensagens lidas
        fetchConversas();
      }
    } catch (error) {
      console.error('Erro ao buscar mensagens:', error);
    }
  };

  const enviarMensagem = async (e) => {
    e.preventDefault();
    if (!novaMensagem.trim() || !conversaAtual) return;
    
    setEnviando(true);
    try {
      const res = await fetch(
        `${API_URL}/api/chat/conversas/${conversaAtual.id}/mensagens?texto=${encodeURIComponent(novaMensagem)}`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      if (res.ok) {
        setNovaMensagem('');
        fetchMensagens();
      } else {
        toast.error('Erro ao enviar mensagem');
      }
    } catch (error) {
      console.error('Erro ao enviar mensagem:', error);
      toast.error('Erro ao enviar mensagem');
    } finally {
      setEnviando(false);
    }
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    } catch {
      return '';
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      const today = new Date();
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      
      if (date.toDateString() === today.toDateString()) {
        return 'Hoje';
      } else if (date.toDateString() === yesterday.toDateString()) {
        return 'Ontem';
      }
      return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit' });
    } catch {
      return '';
    }
  };

  const filteredConversas = conversas.filter(conv => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      conv.empresa_nome?.toLowerCase().includes(search) ||
      conv.participante_1_nome?.toLowerCase().includes(search) ||
      conv.assunto?.toLowerCase().includes(search)
    );
  });

  return (
    <div className="space-y-6" data-testid="admin-chat-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 font-['Outfit']">
            Central de Mensagens
          </h1>
          <p className="text-slate-500 mt-1">
            Gerencie as conversas com as empresas clientes
          </p>
        </div>
        <div className="flex items-center gap-2">
          {totalNaoLidas > 0 && (
            <Badge className="bg-red-500 text-white px-3 py-1">
              {totalNaoLidas} não lida{totalNaoLidas > 1 ? 's' : ''}
            </Badge>
          )}
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => {
              fetchConversas();
              fetchNaoLidas();
            }}
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Atualizar
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 h-[calc(100vh-220px)]">
        {/* Lista de Conversas */}
        <Card className="lg:col-span-1 flex flex-col overflow-hidden">
          <CardHeader className="border-b pb-4 flex-shrink-0">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Buscar empresa..."
                className="pl-9"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                data-testid="chat-search-input"
              />
            </div>
          </CardHeader>
          <CardContent className="flex-1 overflow-y-auto p-0">
            {loading ? (
              <div className="flex items-center justify-center h-40">
                <Loader2 className="w-6 h-6 animate-spin text-emerald-600" />
              </div>
            ) : filteredConversas.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-40 text-center p-4">
                <MessageCircle className="w-10 h-10 text-slate-300 mb-2" />
                <p className="text-slate-500 text-sm">
                  {searchTerm ? 'Nenhuma conversa encontrada' : 'Nenhuma conversa ainda'}
                </p>
              </div>
            ) : (
              <div className="divide-y">
                {filteredConversas.map((conv) => (
                  <button
                    key={conv.id}
                    onClick={() => setConversaAtual(conv)}
                    data-testid={`chat-item-${conv.id}`}
                    className={`w-full p-4 text-left transition-colors hover:bg-slate-50 ${
                      conversaAtual?.id === conv.id ? 'bg-emerald-50 border-l-4 border-emerald-500' : ''
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                        conv.nao_lidas > 0 ? 'bg-emerald-100' : 'bg-slate-100'
                      }`}>
                        <Building className={`w-5 h-5 ${
                          conv.nao_lidas > 0 ? 'text-emerald-600' : 'text-slate-500'
                        }`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <span className={`font-medium truncate ${
                            conv.nao_lidas > 0 ? 'text-slate-900' : 'text-slate-700'
                          }`}>
                            {conv.empresa_nome || conv.participante_1_nome || 'Empresa'}
                          </span>
                          <span className="text-xs text-slate-400 flex-shrink-0">
                            {formatDate(conv.ultima_mensagem_data)}
                          </span>
                        </div>
                        <p className={`text-sm truncate ${
                          conv.nao_lidas > 0 ? 'text-slate-600 font-medium' : 'text-slate-500'
                        }`}>
                          {conv.ultima_mensagem || conv.assunto || 'Nova conversa'}
                        </p>
                      </div>
                      {conv.nao_lidas > 0 && (
                        <Badge className="bg-emerald-600 text-white flex-shrink-0">
                          {conv.nao_lidas}
                        </Badge>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Área de Mensagens */}
        <Card className="lg:col-span-2 flex flex-col overflow-hidden">
          {!conversaAtual ? (
            <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
              <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mb-4">
                <MessageCircle className="w-10 h-10 text-slate-400" />
              </div>
              <h3 className="text-lg font-semibold text-slate-700 mb-2">
                Selecione uma conversa
              </h3>
              <p className="text-slate-500 text-sm max-w-sm">
                Escolha uma conversa na lista ao lado para visualizar e responder às mensagens das empresas
              </p>
            </div>
          ) : (
            <>
              {/* Header da Conversa */}
              <CardHeader className="border-b pb-4 flex-shrink-0">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
                    <Building className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div className="flex-1">
                    <CardTitle className="text-lg">
                      {conversaAtual.empresa_nome || conversaAtual.participante_1_nome || 'Empresa'}
                    </CardTitle>
                    <p className="text-sm text-slate-500">
                      {conversaAtual.assunto || 'Conversa de suporte'}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-slate-400">
                    <Clock className="w-3.5 h-3.5" />
                    {formatDate(conversaAtual.created_at)}
                  </div>
                </div>
              </CardHeader>

              {/* Mensagens */}
              <CardContent className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50">
                {mensagens.length === 0 ? (
                  <div className="text-center text-slate-400 text-sm py-8">
                    Nenhuma mensagem ainda nesta conversa
                  </div>
                ) : (
                  mensagens.map((msg) => {
                    const isMe = msg.remetente_id === user?.id;
                    return (
                      <div
                        key={msg.id}
                        className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-[75%] px-4 py-2.5 rounded-2xl shadow-sm ${
                            isMe
                              ? 'bg-emerald-600 text-white rounded-br-md'
                              : 'bg-white text-slate-800 rounded-bl-md border'
                          }`}
                        >
                          {!isMe && (
                            <p className={`text-xs font-medium mb-1 ${isMe ? 'text-emerald-200' : 'text-emerald-600'}`}>
                              {msg.remetente_nome || 'Empresa'}
                            </p>
                          )}
                          <p className="text-sm whitespace-pre-wrap">{msg.texto}</p>
                          <p className={`text-xs mt-1.5 ${isMe ? 'text-emerald-200' : 'text-slate-400'}`}>
                            {formatTime(msg.created_at)}
                          </p>
                        </div>
                      </div>
                    );
                  })
                )}
                <div ref={messagesEndRef} />
              </CardContent>

              {/* Input de Mensagem */}
              <div className="border-t p-4 bg-white flex-shrink-0">
                <form onSubmit={enviarMensagem} className="flex gap-3">
                  <Input
                    value={novaMensagem}
                    onChange={(e) => setNovaMensagem(e.target.value)}
                    placeholder="Digite sua resposta..."
                    className="flex-1"
                    disabled={enviando}
                    data-testid="chat-message-input"
                  />
                  <Button
                    type="submit"
                    disabled={enviando || !novaMensagem.trim()}
                    className="bg-emerald-600 hover:bg-emerald-700"
                    data-testid="chat-send-button"
                  >
                    {enviando ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Enviar
                      </>
                    )}
                  </Button>
                </form>
              </div>
            </>
          )}
        </Card>
      </div>
    </div>
  );
};

export default ChatAdmin;
